import React, { useEffect, useState, useRef } from 'react';
import { WordInfo } from '../types';
import { WordCard } from './WordCard';
import { useDraggable } from '../hooks/useDraggable';
import './WordPopover.css';

interface WordPopoverProps {
    wordInfo: WordInfo;
    triggerElement: HTMLElement;
    onClose: () => void;
    index: number;
}

export const WordPopover: React.FC<WordPopoverProps> = ({
    wordInfo,
    triggerElement,
    onClose,
    index,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Calculate initial position
    const getInitialPosition = () => {
        // Mobile: CSS handles positioning (bottom sheet), so we just return 0,0
        // The CSS media query will override top/left/transform
        if (window.innerWidth < 768) {
            return { x: 0, y: 0 };
        }

        if (!triggerElement) return { x: window.innerWidth / 2 - 180, y: window.innerHeight / 2 - 250 };

        const rect = triggerElement.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const cardWidth = 360;
        const cardHeight = 500;
        const gap = 20;

        // Get content container bounds to position alongside it
        const contentContainer = document.querySelector('.content-container');
        let contentRect = contentContainer?.getBoundingClientRect();

        let x: number = 0;
        let y = rect.top;

        // Default to centering if no content container found (fallback)
        if (!contentRect) {
            x = rect.right + gap;
            if (x + cardWidth > screenWidth) {
                x = rect.left - cardWidth - gap;
            }
        } else {
            // PC Logic: Position alongside .content-container (same level, not overlapping)
            // Priority: Right side > Left side > Screen edge

            // 1. Try Right Side (preferred)
            if (screenWidth - contentRect.right >= cardWidth + gap) {
                x = contentRect.right + gap;
            }
            // 2. Try Left Side
            else if (contentRect.left >= cardWidth + gap) {
                x = contentRect.left - cardWidth - gap;
            }
            // 3. Screen too narrow - position at right edge with minimal gap
            else {
                x = Math.max(gap, screenWidth - cardWidth - gap);
            }
        }

        // Vertical adjustment
        // Align top with trigger, but keep in bounds
        if (y + cardHeight > screenHeight - gap) {
            y = screenHeight - cardHeight - gap;
        }
        if (y < gap) {
            y = gap;
        }

        return { x, y };
    };

    const { position, handleMouseDown, isDragging } = useDraggable({
        initialPosition: getInitialPosition()
    });

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(onClose, 300);
    };

    // Check if mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    return (
        <>
            {/* Overlay for mobile only */}
            {isMobile && (
                <div
                    className={`popover-overlay ${isVisible ? 'visible' : ''}`}
                    onClick={handleClose}
                />
            )}

            <div
                ref={containerRef}
                className={`popover-glass-container ${isVisible ? 'visible' : ''} ${isDragging ? 'dragging' : ''}`}
                style={{
                    zIndex: 1000 + index,
                    transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${isVisible ? 1 : 0.9})`,
                    opacity: isVisible ? 1 : 0
                }}
                onMouseDown={handleMouseDown}
            >
                <WordCard
                    wordInfo={wordInfo}
                    onClose={handleClose}
                    layout="carousel"
                />
            </div>
        </>
    );
};
